import { useState } from "react";
import { toast } from "sonner";
import { Plus, KeyRound, Send, MoreHorizontal, CalendarDays } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { PageHeader, EmptyState, Loading, StatusBadge } from "@/components/common";
import { TenancyDialog, WelcomeResultDialog } from "@/components/dialogs";
import { api, useFetch, errMsg, money, fmtDate } from "@/lib/api";
import { useCurrency } from "@/context/AuthContext";

function ScheduleSheet({ tid, onClose }) {
  const cur = useCurrency();
  const { data } = useFetch(tid ? `/biz/tenancies/${tid}` : null);
  return (
    <Sheet open={!!tid} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="schedule-sheet">
        <SheetHeader><SheetTitle className="font-display text-2xl">Rent schedule</SheetTitle></SheetHeader>
        {!data ? <Loading /> : (
          <div className="mt-4">
            <p className="text-sm text-slate-500 mb-4">{data.renter_name} · {data.property.name}</p>
            <ul className="divide-y divide-slate-100">
              {data.rent.map((r) => (
                <li key={r.id} className="py-2.5 flex justify-between items-center text-sm">
                  <span>{fmtDate(r.due_date)}</span>
                  <span className="font-mono">{money(r.paid_amount, cur)} / {money(r.amount, cur)}</span>
                  <StatusBadge status={r.status} />
                </li>
              ))}
            </ul>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

export default function Tenancies() {
  const cur = useCurrency();
  const { data, reload } = useFetch("/biz/tenancies");
  const { data: props } = useFetch("/biz/properties");
  const [units, setUnits] = useState([]);
  const [open, setOpen] = useState(false);
  const [welcome, setWelcome] = useState(null);
  const [sched, setSched] = useState(null);

  const openNew = async () => {
    const all = await Promise.all((props || []).map((p) => api.get(`/biz/properties/${p.id}`).then((r) => r.data.units)));
    setUnits(all.flat());
    setOpen(true);
  };
  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const resend = async (t) => {
    try {
      const r = await api.post(`/biz/tenancies/${t.id}/resend-welcome`, { origin_url: window.location.origin });
      setWelcome(r.data);
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="tenancies-page">
      <PageHeader eyebrow="Lettings" title="Tenancies" subtitle="Each tenancy creates a rent schedule and gives the renter a portal login."
        actions={<Button onClick={openNew} disabled={!props?.length} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="add-tenancy-button"><Plus className="w-4 h-4 mr-1" />New tenancy</Button>} />
      {!data ? <Loading /> : data.length === 0 ? (
        <EmptyState icon={KeyRound} title="No tenancies yet" text={props?.length ? "Create a tenancy to start tracking rent." : "Add a property first, then create a tenancy."} />
      ) : (
        <div className="panel p-0 overflow-x-auto">
          <Table>
            <TableHeader><TableRow><TableHead>Renter</TableHead><TableHead>Property</TableHead><TableHead>Period</TableHead><TableHead>Rent</TableHead><TableHead>Welcome pack</TableHead><TableHead>Status</TableHead><TableHead /></TableRow></TableHeader>
            <TableBody>
              {data.map((t) => (
                <TableRow key={t.id} data-testid={`tenancy-row-${t.id}`}>
                  <TableCell className="font-semibold">{t.renter_name}<div className="text-xs text-slate-500 font-normal">{t.renter_email}</div></TableCell>
                  <TableCell className="text-sm">{t.property_name}{t.unit_name && <div className="text-xs text-slate-500">{t.unit_name}</div>}</TableCell>
                  <TableCell className="text-sm whitespace-nowrap">{fmtDate(t.start_date)} – {fmtDate(t.end_date)}</TableCell>
                  <TableCell className="whitespace-nowrap">{money(t.rent_amount, cur)}<div className="text-xs text-slate-500">{t.rent_frequency}</div></TableCell>
                  <TableCell>{t.welcome_status ? <StatusBadge status={t.welcome_status} label={t.welcome_status === "skipped" ? "not emailed" : t.welcome_status} /> : <span className="text-xs text-slate-400">—</span>}</TableCell>
                  <TableCell><StatusBadge status={t.status} testid={`tenancy-status-${t.id}`} /></TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" data-testid={`tenancy-actions-${t.id}`}><MoreHorizontal className="w-4 h-4" /></Button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => setSched(t.id)} data-testid={`tenancy-schedule-${t.id}`}><CalendarDays className="w-4 h-4 mr-2" />Rent schedule</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => resend(t)} data-testid={`tenancy-resend-${t.id}`}><Send className="w-4 h-4 mr-2" />Resend welcome pack</DropdownMenuItem>
                        {t.status === "active" && <DropdownMenuItem onClick={() => act(() => api.patch(`/biz/tenancies/${t.id}`, { status: "notice" }), "Marked as notice given")} data-testid={`tenancy-notice-${t.id}`}>Notice given</DropdownMenuItem>}
                        {t.status !== "ended" && <DropdownMenuItem onClick={() => act(() => api.patch(`/biz/tenancies/${t.id}`, { status: "ended", end_date: new Date().toISOString().slice(0, 10) }), "Tenancy ended")} data-testid={`tenancy-end-${t.id}`}>End tenancy today</DropdownMenuItem>}
                        <DropdownMenuItem className="text-rose-600" onClick={() => window.confirm("Delete this tenancy and its rent records?") && act(() => api.delete(`/biz/tenancies/${t.id}`), "Tenancy deleted")} data-testid={`tenancy-delete-${t.id}`}>Delete</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <TenancyDialog open={open} onOpenChange={setOpen} properties={props} units={units} onCreated={(r) => { reload(); setWelcome(r.welcome); }} />
      <WelcomeResultDialog welcome={welcome} onClose={() => setWelcome(null)} />
      <ScheduleSheet tid={sched} onClose={() => setSched(null)} />
    </div>
  );
}
